/**
 * Cumulative / month-over-month progress helpers (local IndexedDB data).
 */
import { formatINR } from './leakBuckets';

function parseAmount(str) {
    if (typeof str === 'number') return Math.abs(str);
    if (!str) return 0;
    return Math.abs(parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
}

function isDebit(txn) {
    return txn.type && String(txn.type).toUpperCase() === 'DEBIT';
}

function monthLabel(prefix) {
    const [y, m] = prefix.split('-');
    return new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1)
        .toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });
}

/**
 * @returns {{ months: Array<{ prefix, label, spent, byCategory }>, monthCount, momChangePct, trend }}
 */
export function getMonthlyProgress(transactions, limit = 6) {
    const map = {};
    for (const txn of transactions || []) {
        if (!txn.date || txn.date.length < 7) continue;
        if (!isDebit(txn)) continue;
        const amount = parseAmount(txn.amount);
        if (amount <= 0) continue;
        const prefix = txn.date.substring(0, 7);
        if (!map[prefix]) map[prefix] = { spent: 0, byCategory: {} };
        map[prefix].spent += amount;
        const cat = txn.category || 'Uncategorized';
        map[prefix].byCategory[cat] = (map[prefix].byCategory[cat] || 0) + amount;
    }

    const prefixes = Object.keys(map).sort();
    const months = prefixes.slice(-limit).map(prefix => ({
        prefix,
        label: monthLabel(prefix),
        spent: Math.round(map[prefix].spent),
        byCategory: map[prefix].byCategory,
    }));

    let momChangePct = null;
    if (months.length >= 2) {
        const prev = months[months.length - 2].spent;
        const curr = months[months.length - 1].spent;
        if (prev > 0) momChangePct = Math.round(((curr - prev) / prev) * 100);
    }

    let trend = 'building';
    if (months.length >= 3) {
        const a = months[months.length - 3].spent;
        const b = months[months.length - 2].spent;
        const c = months[months.length - 1].spent;
        if (c < b && b < a) trend = 'improving';
        else if (c > b && b > a) trend = 'rising';
        else trend = 'mixed';
    }

    return {
        months,
        monthCount: prefixes.length,
        momChangePct,
        trend,
        maxSpent: months.reduce((m, x) => Math.max(m, x.spent), 0) || 1,
    };
}

export function getProgressCopy(progress) {
    const { monthCount, momChangePct, months } = progress;
    if (monthCount === 0) {
        return { title: 'Progress', body: 'Add a statement to start.' };
    }
    if (monthCount === 1) {
        return {
            title: 'This month',
            body: `${formatINR(months[0].spent)} spent`,
        };
    }
    if (momChangePct == null) {
        return {
            title: `${monthCount} months`,
            body: 'Keep uploading each month',
        };
    }
    const dir = momChangePct <= 0
        ? `${Math.abs(momChangePct)}% lower than last month`
        : `${momChangePct}% higher than last month`;
    return {
        title: monthCount === 2 ? 'vs last month' : `${monthCount} months`,
        body: dir,
    };
}
